import { useForm } from 'react-hook-form'
import { useRef, useLayoutEffect } from 'react'
import FunnelProgress from './funnel-progress'
import FormStep from './form-step'
import { setConfig, updateConsents } from '@uswitch/rvuconsent'
import { timeout, useComponentTracking } from '@uswitch/ev-utils/component-events'
import whitelist from '../../../utils/form-whitelist'

const Form = props => {
  const {
    schema: {
      steps = [],
      action,
      method,
      formName,
      formId,
      defaultInputs,
      showProgressBar = false,
      withBackButton = false,
      variant = 'default',
      disableButtonUntilValid = false,
      rvuconsent = null,
    },
    apiPath,
    clientId,
    brand,
    submit,
    formType,
    addressLookup,
    activeIndex,
    goForward,
    goBack,
    goToFirst,
    goToLast,
    goToPage,
    goToUrl,
    query = {},
    completionState,
    updateCompletionState,
    editFromSummary,
    trackInteraction = () => {},
    segmentTracking,
  } = props

  const numberFormattedQuery = Object.entries(query).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [key]: !isNaN(parseInt(val, 10)) ? new Intl.NumberFormat('en-GB').format(val) : val,
    }),
    {},
  )

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    triggerValidation,
    formState,
    watch,
    getValues,
  } = useForm({
    mode: 'all',
    defaultValues: {
      ...defaultInputs,
      ...numberFormattedQuery,
    },
  })
  const form = useRef(null)
  const { isDirty, isValid } = formState

  const trackComponent = useComponentTracking()

  const trackAndSubmit = async data => {
    trackInteraction('GA Form Submitted', 'Form Interaction', `${activeIndex}`)
    await timeout(
      trackComponent('Form Event', 'formSubmitted', {
        formName,
        formId,
        ...whitelist(data, steps),
      }),
    )

    const submitForm = async data => {
      if (segmentTracking) {
        await segmentTracking()
      }

      return submit ? submit(data) : form.current.submit()
    }

    if (!rvuconsent) return submitForm(data)

    Promise.all(
      steps
        .reduce((acc, it) => acc.concat(it.fields), [])
        .map(async field => {
          if (!field.consentName) return
          if (!data[`${field.name}-checkbox`] && !field.optionalConsent) return

          const noConsent = !data[`${field.name}-checkbox`]

          return updateConsents({
            email: data[field.name],
            consents: noConsent
              ? []
              : [
                  {
                    name: field.consentName,
                    status: data[`${field.name}-checkbox`],
                    source: rvuconsent.source,
                    sourceUrl: window.location.href,
                  },
                ],
          })
        }),
    ).then(_ => submitForm(data))
  }

  const progressPhases = steps.reduce((acc, { id, funnelTitle = '' }) => {
    return [...acc, { key: id, title: funnelTitle }]
  }, [])
  const progressIncrement = Math.round(100 / steps.length)

  // set it globally before render so you can access it elsewhere
  useLayoutEffect(() => {
    if (rvuconsent) {
      setConfig({
        apiPath: apiPath,
        clientId: clientId,
        brand,
      })
    }
  }, [])

  return (
    <form
      name={`multi-step-${formName}`}
      action={action}
      method={method}
      onSubmit={handleSubmit(trackAndSubmit)}
      ref={form}
      sx={{ variant: `elements.form.variants.${variant}.formWrapper` }}
      id={formId}
    >
      <If condition={showProgressBar}>
        <div
          sx={{
            variant: `elements.form.variants.${variant}.funnel`,
            mb: withBackButton && activeIndex !== 0 ? '0' : 'md',
          }}
        >
          <FunnelProgress
            phases={progressPhases}
            currentPhaseKey={progressPhases[activeIndex]?.key || ''}
            progress={(progressIncrement * activeIndex) / 100}
            variant='percentage'
          />
        </div>
      </If>
      {steps.map((step, i) => {
        return (
          <FormStep
            stepIndex={i}
            key={i}
            {...{
              step,
              register,
              errors,
              disableButtonUntilValid,
              setValue,
              triggerValidation,
              formType,
              activeIndex,
              goForward,
              goBack,
              goToFirst,
              goToLast,
              goToPage,
              goToUrl,
              isDirty,
              isValid,
              watch,
              completionState,
              updateCompletionState,
              getValues,
              editFromSummary,
              withBackButton,
              variant,
              query,
              handleSubmit,
              trackAndSubmit,
              addressLookup,
            }}
            {...props}
          />
        )
      })}
    </form>
  )
}

export default Form
